import { useMemo, useState } from "react";
import { SudoButton } from "../../sudokit/components/SudoButton";
import type { Diagnosis, RestoreStructureResult } from "../../api/repair";
import type { UploadedBackup } from "./repairReducer";
import { formatBackupTime } from "./downloads";
import { RestoreStructureDetails } from "./RestoreStructureDetails";

type Row = { label: string; count: number; severity: "red" | "yellow" | "green"; detail?: string };

// Subtle left accent stripe rather than a full border — quieter visual weight.
const ROW_ACCENT: Record<Row["severity"], string> = {
  red: "bg-red-50/60 text-red-900 border-l-red-400",
  yellow: "bg-amber-50/60 text-amber-900 border-l-amber-400",
  green: "bg-emerald-50/60 text-emerald-900 border-l-emerald-400",
};

function makeRows(diagnosis: Diagnosis, uploadedBackup: UploadedBackup | null): Row[] {
  const missing = diagnosis.missingFromTree.length;
  const external = diagnosis.externalDocs.length;
  const folderCount = diagnosis.folders.count;
  const pitrBackup = diagnosis.folders.backup;
  const effective = uploadedBackup
    ? { available: true, folderCount: uploadedBackup.folderCount, documentCount: uploadedBackup.documentCount, label: `upload: ${uploadedBackup.name}` }
    : { available: pitrBackup.available, folderCount: pitrBackup.folderCount, documentCount: pitrBackup.documentCount, label: formatBackupTime(pitrBackup.readTime) };

  // Trash is intentionally omitted — it's informational only (no repair step
  // acts on it) and showing a row the admin can't act on just adds noise.
  const rows: Row[] = [
    {
      label: `${missing} document${missing === 1 ? "" : "s"} missing from tree`,
      count: missing,
      severity: missing > 0 ? "red" : "green",
    },
    {
      label: `${external} external document${external === 1 ? "" : "s"} contaminating this project`,
      count: external,
      severity: external > 0 ? "red" : "green",
    },
  ];

  const backupHasFolders = effective.available && effective.folderCount > 0;
  if (folderCount > 0 || backupHasFolders) {
    const label =
      folderCount === 0
        ? `No folders (backup available from ${effective.label})`
        : backupHasFolders
        ? `${folderCount} folder${folderCount === 1 ? "" : "s"} — backup from ${effective.label} may differ`
        : `${folderCount} folder${folderCount === 1 ? "" : "s"}`;
    rows.push({
      label,
      count: folderCount,
      // Yellow whenever a backup is available — the admin should dry-run to see if
      // restoring would remap any documents, regardless of current folder count.
      severity: backupHasFolders ? "yellow" : "green",
      detail: effective.available ? `Backup contains ${effective.folderCount} folder(s), ${effective.documentCount} document(s)` : undefined,
    });
  }

  return rows;
}

function defaultPitrInput(): string {
  // Default to 1 hour ago so we're inside the 7-day automatic-backup window and
  // away from live writes. "YYYY-MM-DDTHH:MM" for <input type="datetime-local">.
  const d = new Date(Date.now() - 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Props = {
  diagnosis: Diagnosis | null;
  loading: boolean;
  error: string | null;
  uploadedBackup: UploadedBackup | null;
  pitrTimestamp: number | null;
  folderStepResult: RestoreStructureResult | null;
  onRun: (timestamp?: number | null) => void;
  onUploadBackup: (file: File | null) => void;
  onDownloadReport: () => void;
  onDownloadBackupJson: () => void;
  onContinue: () => void;
};

export function DiagnosisStep({
  diagnosis,
  loading,
  error,
  uploadedBackup,
  pitrTimestamp,
  folderStepResult,
  onRun,
  onUploadBackup,
  onDownloadReport,
  onDownloadBackupJson,
  onContinue,
}: Props) {
  const [pitrInput, setPitrInput] = useState(defaultPitrInput);

  const maxDate = useMemo(() => {
    // Backups <1 hour ago are unreliable; cap the picker there.
    const d = new Date(Date.now() - 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const minDate = useMemo(() => {
    // 7-day automatic-backup window.
    const d = new Date(Date.now() - (7 * 24 - 1) * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onUploadBackup(file);
    e.target.value = "";
  };

  // Backup section is hidden by default — most repairs don't need one. Opens
  // automatically if a backup is already active (uploaded or timestamp set).
  const hasActiveBackup = !!uploadedBackup || pitrTimestamp != null;
  const [backupPickerOpen, setBackupPickerOpen] = useState(hasActiveBackup);
  // Once an active backup is picked and its preview has loaded, collapse the
  // source controls (datetime picker + upload) into a compact bar. Admin can
  // click "Change backup" to re-expose them.
  const [forceShowPicker, setForceShowPicker] = useState(false);
  const showingPicker = !hasActiveBackup || forceShowPicker;

  // If admin picked a backup but the preview shows no document moves, nothing
  // needs to happen — structure already matches. In that case the picked
  // backup shouldn't count as an actionable item.
  const folderMoveCount = folderStepResult?.moves?.length ?? null;
  const backupWantsChanges =
    (!!uploadedBackup || pitrTimestamp != null) &&
    (folderMoveCount === null || folderMoveCount > 0);

  const hasActionableIssues =
    !!diagnosis &&
    (diagnosis.missingFromTree.length > 0 ||
      diagnosis.externalDocs.length > 0 ||
      backupWantsChanges);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 1 — Diagnose</h3>
        <p className="text-sm text-gray-600 mt-1">
          Project audit + trash inspector run automatically when the modal opens.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50/60 border-l-2 border-l-red-400 p-3 text-sm text-red-900">
          {error}
        </div>
      )}

      {!diagnosis && loading && (
        <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-600 flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-gray-300 animate-pulse" />
          Running diagnosis…
        </div>
      )}

      {diagnosis && (
        <div className="space-y-1.5">
          {makeRows(diagnosis, uploadedBackup).map((row, i) => (
            <div key={i} className={`border-l-2 rounded-md px-3 py-2 ${ROW_ACCENT[row.severity]}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{row.label}</span>
                <span className="text-xs opacity-70 whitespace-nowrap">{row.detail ?? ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {diagnosis && !hasActionableIssues && (
        <div className="rounded-md bg-emerald-50/60 border-l-2 border-l-emerald-400 p-3">
          <p className="text-sm font-medium text-emerald-900">Project looks clean — nothing to repair.</p>
          <p className="text-xs text-emerald-700/80 mt-1">
            If you need to recover folder structure from an earlier point in time, open the backup picker below and pick a time.
          </p>
        </div>
      )}

      {/* Optional folder-restore path lives below the summary — admin only
          reaches for it when they need to recover folder structure from a
          backup. Toolbar (re-run, download) sits below this. */}
      <div className="pt-4 mt-2 border-t border-gray-100">
        {!backupPickerOpen ? (
          <button
            type="button"
            onClick={() => setBackupPickerOpen(true)}
            className="w-full group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
          >
            <span className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 group-hover:bg-gray-200 transition-colors text-gray-500 text-sm">
                +
              </span>
              <span>
                <span className="text-sm font-medium text-gray-800">Restore folders from a backup</span>
                <span className="text-xs text-gray-500 block">Only needed if folder structure needs recovery.</span>
              </span>
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Optional</span>
          </button>
        ) : (
          <div className="rounded-lg bg-gray-50/80 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-800">Restore folders from a backup</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {hasActiveBackup
                    ? uploadedBackup
                      ? `Using upload: ${uploadedBackup.name}`
                      : "Using automatic backup"
                    : "Pick a source below. Only needed if folder structure needs recovery."}
                </p>
              </div>
              <SudoButton variant="tertiary" size="default" onClick={() => { setBackupPickerOpen(false); setForceShowPicker(false); }}>
                Hide
              </SudoButton>
            </div>

            {showingPicker ? (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
                <div className={`rounded-md p-3 bg-white ring-1 ring-gray-200 transition-opacity ${uploadedBackup ? "opacity-40" : ""}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Option A</span>
                    <span className="text-sm font-medium text-gray-800">Automatic backup</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm mb-2">
                    <input
                      type="datetime-local"
                      value={pitrInput}
                      onChange={(e) => setPitrInput(e.target.value)}
                      min={minDate}
                      max={maxDate}
                      className="rounded px-2 py-1 text-sm flex-1 ring-1 ring-gray-200 focus:ring-gray-400 focus:outline-none"
                      disabled={!!uploadedBackup}
                    />
                    <span className="text-xs text-gray-500">local</span>
                  </label>
                  <SudoButton
                    variant="tertiary"
                    size="default"
                    disabled={loading || !!uploadedBackup}
                    onClick={() => {
                      const unix = Math.floor(new Date(pitrInput).getTime() / 1000);
                      if (!Number.isFinite(unix)) return;
                      setForceShowPicker(false);
                      onRun(unix);
                    }}
                  >
                    Use this automatic backup
                  </SudoButton>
                </div>

                <div className="flex items-center justify-center">
                  <span className="text-[11px] font-semibold text-gray-400 px-2 py-1 rounded-full bg-white ring-1 ring-gray-200">OR</span>
                </div>

                <div className="rounded-md p-3 bg-white ring-1 ring-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Option B</span>
                    <span className="text-sm font-medium text-gray-800">Upload backup JSON</span>
                  </div>
                  {uploadedBackup ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700 break-all">
                        <span className="font-medium">{uploadedBackup.name}</span>
                        <span className="text-gray-500"> — {uploadedBackup.folderCount} folders, {uploadedBackup.documentCount} docs</span>
                      </p>
                      <SudoButton variant="tertiary" size="default" onClick={() => { setForceShowPicker(false); onUploadBackup(null); }}>
                        Remove upload
                      </SudoButton>
                    </div>
                  ) : (
                    <label className="inline-flex">
                      <input type="file" accept=".json,application/json" className="hidden" onChange={handleFileChange} />
                      <span className="px-3 py-1 text-sm rounded cursor-pointer bg-white ring-1 ring-gray-200 hover:ring-gray-400 transition-colors">
                        Choose JSON file…
                      </span>
                    </label>
                  )}
                </div>
              </div>
            ) : (
              // Compact "active source" bar — shown once a backup is picked and
              // the tree is rendered below. Admin can download the raw JSON
              // (to preserve it before its 7-day expiry) or swap sources.
              <div className="flex items-center justify-between gap-3 rounded-md bg-white ring-1 ring-gray-200 px-3 py-2">
                <span className="text-xs text-gray-600 truncate">
                  <span className="font-medium text-gray-800">Source:</span>{" "}
                  {uploadedBackup
                    ? `Upload — ${uploadedBackup.name} (${uploadedBackup.folderCount} folders, ${uploadedBackup.documentCount} docs)`
                    : `Automatic backup from ${formatBackupTime(diagnosis?.folders.backup.readTime)}`}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <SudoButton variant="tertiary" size="default" onClick={onDownloadBackupJson}>
                    Download backup
                  </SudoButton>
                  <SudoButton variant="tertiary" size="default" onClick={() => setForceShowPicker(true)}>
                    Change backup
                  </SudoButton>
                </div>
              </div>
            )}

            {/* Inline preview so admin can confirm the backup before leaving
                the diagnose screen. Shown once the dry-run has auto-loaded. */}
            {hasActiveBackup && folderStepResult && (
              <div className="pt-3 border-t border-gray-200">
                <RestoreStructureDetails result={folderStepResult} diagnosis={diagnosis} />
              </div>
            )}
            {hasActiveBackup && !folderStepResult && (
              <p className="pt-3 text-xs text-gray-500 border-t border-gray-200 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300 animate-pulse" />
                Computing preview…
              </p>
            )}
          </div>
        )}
      </div>

      {diagnosis && (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            Last run <span className="text-gray-600">{new Date(diagnosis.auditedAt).toLocaleString()}</span>
          </span>
          <div className="flex items-center gap-2">
            <SudoButton variant="tertiary" size="default" disabled={loading} onClick={() => onRun(pitrTimestamp)}>
              {loading ? "Running…" : "Re-run"}
            </SudoButton>
            <SudoButton variant="tertiary" size="default" onClick={onDownloadReport}>
              Download report
            </SudoButton>
          </div>
        </div>
      )}

      {diagnosis && hasActionableIssues && (
        <div className="flex justify-end">
          <SudoButton variant="primary" size="default" onClick={onContinue}>
            Continue to repair
          </SudoButton>
        </div>
      )}
    </div>
  );
}
